import { useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import {
  Bold, Italic, Underline, Heading1, Heading2, List, ListOrdered,
  Link as LinkIcon, Quote, Minus, Undo, Redo, Code
} from "lucide-react";
import { Separator } from "@/components/ui/separator";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

const TOOLBAR = [
  { cmd: "bold", icon: Bold, label: "Bold" },
  { cmd: "italic", icon: Italic, label: "Italic" },
  { cmd: "underline", icon: Underline, label: "Underline" },
  { type: "sep" },
  { cmd: "formatBlock", arg: "h2", icon: Heading1, label: "Heading 1" },
  { cmd: "formatBlock", arg: "h3", icon: Heading2, label: "Heading 2" },
  { type: "sep" },
  { cmd: "insertUnorderedList", icon: List, label: "Bullet List" },
  { cmd: "insertOrderedList", icon: ListOrdered, label: "Numbered List" },
  { cmd: "formatBlock", arg: "blockquote", icon: Quote, label: "Quote" },
  { type: "sep" },
  { cmd: "createLink", icon: LinkIcon, label: "Insert Link", prompt: true },
  { cmd: "insertHorizontalRule", icon: Minus, label: "Divider" },
  { type: "sep" },
  { cmd: "undo", icon: Undo, label: "Undo" },
  { cmd: "redo", icon: Redo, label: "Redo" },
];

export default function RichTextEditor({ value, onChange, placeholder }) {
  const editorRef = useRef(null);
  const isInitialized = useRef(false);

  const handleRef = useCallback((node) => {
    if (node && !isInitialized.current) {
      editorRef.current = node;
      if (value && node.innerHTML !== value) {
        node.innerHTML = value;
      }
      isInitialized.current = true;
    }
  }, [value]);

  const exec = (cmd, arg) => {
    editorRef.current?.focus();
    if (cmd === "createLink") {
      const url = prompt("Enter URL:", "https://");
      if (url) document.execCommand(cmd, false, url);
    } else if (arg) {
      document.execCommand(cmd, false, arg);
    } else {
      document.execCommand(cmd, false, null);
    }
    handleInput();
  };

  const handleInput = () => {
    if (editorRef.current) {
      onChange?.(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = e.clipboardData.getData("text/html") || e.clipboardData.getData("text/plain");
    document.execCommand("insertHTML", false, text);
    handleInput();
  };

  return (
    <div className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800" data-testid="rich-text-editor">
      {/* Toolbar */}
      <TooltipProvider delayDuration={300}>
        <div className="flex flex-wrap items-center gap-0.5 p-2 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/80">
          {TOOLBAR.map((item, i) => {
            if (item.type === "sep") return <Separator key={i} orientation="vertical" className="h-6 mx-1" />;
            return (
              <Tooltip key={i}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-slate-200 dark:hover:bg-slate-700"
                    onClick={() => exec(item.cmd, item.arg)}
                    data-testid={`rte-${item.cmd}`}
                  >
                    <item.icon className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="text-xs">{item.label}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>

      {/* Editor area */}
      <div
        ref={handleRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        className="min-h-[300px] max-h-[600px] overflow-y-auto p-4 font-sans text-base text-foreground leading-relaxed focus:outline-none prose prose-slate dark:prose-invert max-w-none [&_h2]:text-2xl [&_h2]:font-serif [&_h2]:font-bold [&_h2]:mt-6 [&_h2]:mb-3 [&_h3]:text-xl [&_h3]:font-serif [&_h3]:font-bold [&_h3]:mt-4 [&_h3]:mb-2 [&_blockquote]:border-l-4 [&_blockquote]:border-[#1E88E5] [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_ul]:list-disc [&_ul]:pl-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_a]:text-[#1E88E5] [&_a]:underline [&_hr]:my-4 [&_hr]:border-slate-200"
        data-placeholder={placeholder || "Start writing your article..."}
        suppressContentEditableWarning
        data-testid="rte-content-area"
      />

      {/* Empty state placeholder */}
      <style>{`
        [data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #94a3b8;
          pointer-events: none;
          display: block;
        }
      `}</style>
    </div>
  );
}
